<div class="fvv">
  <mat-toolbar class="fvv__toolbar">
    <span class="fvv__title">Flat View — {{ data.patternName }}</span>
    <span class="fvv__spacer"></span>

    <span class="fvv__group">
      <span
        class="fvv__group-label"
        matTooltip="Which way the part travels, as you stand at the operator side. The leading edge is drawn at the exit end."
      >
        Flow
      </span>
      <button
        mat-icon-button
        matTooltip="Right-to-left machine (part exits left)"
        [class.fvv__active]="!flowRight"
        [disabled]="!canEditOrientation()"
        (click)="setFlow(false)"
      >
        <mat-icon>west</mat-icon>
      </button>
      <button
        mat-icon-button
        matTooltip="Left-to-right machine (part exits right)"
        [class.fvv__active]="flowRight"
        [disabled]="!canEditOrientation()"
        (click)="setFlow(true)"
      >
        <mat-icon>east</mat-icon>
      </button>
    </span>

    <span class="fvv__group">
      <span
        class="fvv__group-label"
        matTooltip="Which side of the machine is +, relative to where you stand. The operator side is always the bottom of the drawing."
      >
        + side
      </span>
      <button
        mat-icon-button
        matTooltip="Machine + is away from the operator (drawn up)"
        [class.fvv__active]="plusFar"
        [disabled]="!canEditOrientation()"
        (click)="setPlusFar(true)"
      >
        <mat-icon>north</mat-icon>
      </button>
      <button
        mat-icon-button
        matTooltip="Machine + is toward the operator (drawn down)"
        [class.fvv__active]="!plusFar"
        [disabled]="!canEditOrientation()"
        (click)="setPlusFar(false)"
      >
        <mat-icon>south</mat-icon>
      </button>
    </span>

    <span class="fvv__group">
      <button
        mat-icon-button
        matTooltip="Show all dimensions (hover a hole for its own)"
        [disabled]="!view"
        [class.fvv__active]="showDims"
        (click)="toggleDims()"
      >
        <mat-icon>straighten</mat-icon>
      </button>
      <button
        mat-icon-button
        [matTooltip]="'formedView' | translate"
        [disabled]="!view"
        (click)="open3d()"
      >
        <mat-icon>view_in_ar</mat-icon>
      </button>
      <button
        mat-icon-button
        matTooltip="Zoom out"
        (click)="zoomOut()"
      >
        <i class="icon icon-minus-box"></i>
      </button>
      <button
        mat-icon-button
        matTooltip="Fit to view (true scale)"
        (click)="fit()"
      >
        <i class="icon icon-fullscreen"></i>
      </button>
      <button
        mat-icon-button
        matTooltip="Zoom in"
        (click)="zoomIn()"
      >
        <i class="icon icon-plus-box"></i>
      </button>
    </span>

    <button
      mat-icon-button
      matTooltip="Close"
      (click)="close()"
    >
      <i class="icon icon-close"></i>
    </button>
  </mat-toolbar>

  @if (view; as v) {
  <div class="fvv__meta">
    @if (v.macroInstance) {
    <span
      class="fvv__meta-item fvv__meta-item--macro"
      matTooltip="Macros are never solved on their own, so this is one call to the macro, as a pattern calling it would place it. Part printing and tooling code pattern rules are left out."
    >
      {{ macroAnnotation }}
    </span>
    }
    <span class="fvv__meta-item">Machine {{ v.machineNumber }}</span>
    @if (v.toolingCode) {
    <span class="fvv__meta-item">Tooling {{ v.toolingCode }}</span>
    }
    <span class="fvv__meta-item">
      {{ fmt(v.lengthIn) }} × {{ v.widthIn != null ? fmt(v.widthIn) : '(no width)' }}
    </span>
    <span
      class="fvv__meta-item fvv__meta-item--flips"
      matTooltip="The drawing is post-flip, as the machine runs it. Hole tooltips show the pre-flip segment math."
    >
      {{ flipAnnotation }}
    </span>
    @if (v.ignoredShapes?.length || v.ignoredMacros?.length) {
    <span
      class="fvv__meta-item fvv__meta-item--ignored"
      matTooltip="This machine's shape table ignores these shapes and shape macros, so they are not drawn and not punched here. Edit the lists on the machine's Ignored Shapes tab."
    >
      Ignored: {{ ignoredSummary }}
      @if (v.allShapesIgnored) {
      (every shape — nothing to solve)
      }
    </span>
    }
    @if (v.stripOffset) {
    <span
      class="fvv__meta-item"
      matTooltip="The tooling code's strip offset: the strip is shifted this far toward machine + while the controller stays shift-unaware, so the solver adds it onto every emitted controller offset. The machine-center guide shows where machine Y 0 falls on the strip."
    >
      Strip offset {{ fmt(v.stripOffset) }}
    </span>
    }
    @if (v.geometry; as g) {
    <span
      class="fvv__meta-item fvv__meta-item--geometry"
      matTooltip="FinWidth is the tooling's formed/nominal value; the flat web the resolution uses is FinWidth + 2 × bend correction."
    >
      Web {{ fmt(g.flatWebWidth) }} flat{{ corrAnnotation(g) }}
      · Flanges {{ fmt(g.flangeHeightPlus) }} / {{ fmt(g.flangeHeightMinus) }} · Δ
      {{ fmt(g.webCenterOffset) }}{{ g.webCenterOffsetIsOverride ? ' (measured)' : '' }}
      @if (g.cellGaugePlus || g.cellGaugeMinus) {
      <span>
        · cells
        {{ g.cellGaugePlus ? g.cellGaugePlus + 'G' : '*' }} /
        {{ g.cellGaugeMinus ? g.cellGaugeMinus + 'G' : '*' }}
      </span>
      }
      @if (g.webCenterOffsetRule) {
      <span> · Δ rule "{{ g.webCenterOffsetRule }}"</span>
      }
      @if (g.source === 'Tooling') {
      <span
        matTooltip="This pattern uses machine references, so no profile geometry was resolved for the solve. These bend lines are derived from the tooling's dimensions for display."
      >
        · from tooling
      </span>
      }
    </span>
    }
  </div>

  @if (v.status) {
  <div class="fvv__error">
    <div class="fvv__error-head">
      <b translate="{{ v.status }}"></b>
      @if (v.partial) {
      <span> — drawing the machine-referenced holes that still resolve</span>
      } @else {
      <span> — nothing drawable for this row</span>
      }
    </div>
    @if (v.error) {
    <pre class="fvv__error-detail">{{ v.error }}</pre>
    }
  </div>
  } }

  @if (loadError) {
  <div class="fvv__error">
    <div class="fvv__error-head">{{ loadError }}</div>
  </div>
  }

  <div class="fvv__viewport">
    <div
      class="fvv__scroll"
      #scroller
      (wheel)="onWheel($event)"
      (scroll)="onScroll()"
    >
    @if (loading) {
    <div class="fvv__loading">
      <mat-spinner diameter="42"></mat-spinner>
    </div>
    } @if (view) {
    <svg
      class="fvv__canvas"
      [attr.width]="svgW"
      [attr.height]="svgH"
    >
      <defs>
        <marker
          id="fvvDimArrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path
            d="M 0 0 L 10 5 L 0 10 z"
            class="dim__arrow"
          />
        </marker>
      </defs>

      <!-- The strip (flat blank). Dashed when the material has no width. -->
      <rect
        class="strip"
        [class.strip--unknown-width]="!widthKnown"
        [attr.x]="strip.x"
        [attr.y]="strip.y"
        [attr.width]="strip.w"
        [attr.height]="strip.h"
      >
        <title>{{ stripTooltip }}</title>
      </rect>

      <!-- Guide lines from the solve's own ProfileGeometry -->
      @for (g of guides; track $index) {
      <line
        [class]="'guide__line ' + g.cls"
        [attr.x1]="g.x1"
        [attr.y1]="g.y"
        [attr.x2]="g.x2"
        [attr.y2]="g.y"
      >
        <title>{{ g.tooltip }}</title>
      </line>
      }

      <!-- Guide labels, pinned to the visible left edge while the length scrolls -->
      <g [attr.transform]="'translate(' + labelShiftX + ',0)'">
        @for (g of guides; track $index) {
        <text
          [class]="'guide__label ' + g.cls"
          [attr.x]="10"
          [attr.y]="g.labelY"
        >
          {{ g.label }}
          <title>{{ g.tooltip }}</title>
        </text>
        }
        <text
          class="operator-label"
          [attr.x]="10"
          [attr.y]="svgH - 8"
        >
          Operator side
          <title>The drawing is a plan view from where the operator stands: near side at the bottom.</title>
        </text>
      </g>

      <!-- Leading edge (X = 0) at the exit end; part length at the trailing end -->
      <line
        class="edge"
        [attr.x1]="leadingEdgeX"
        [attr.y1]="strip.y - 14"
        [attr.x2]="leadingEdgeX"
        [attr.y2]="strip.y + strip.h + 14"
      >
        <title>Leading edge (X = 0)</title>
      </line>
      <text
        class="edge__label"
        [attr.x]="leadingEdgeX + (flowRight ? -5 : 5)"
        [attr.y]="strip.y - 18"
        [attr.text-anchor]="flowRight ? 'end' : 'start'"
      >
        Leading edge {{ flowRight ? '→' : '←' }}
      </text>
      <text
        class="edge__label"
        [attr.x]="trailingEdgeX + (flowRight ? 5 : -5)"
        [attr.y]="strip.y - 18"
        [attr.text-anchor]="flowRight ? 'start' : 'end'"
      >
        {{ lengthLabel }}
      </text>

      <!-- A macro viewed on its own: where its one call is -->
      @if (callLine; as c) {
      <line
        class="call"
        [attr.x1]="c.x"
        [attr.y1]="strip.y - 10"
        [attr.x2]="c.x"
        [attr.y2]="strip.y + strip.h + 10"
      >
        <title>{{ c.tooltip }}</title>
      </line>
      <text
        class="call__label"
        [attr.x]="c.x"
        [attr.y]="strip.y - 5"
      >
        Macro call
        <title>{{ c.tooltip }}</title>
      </text>
      }

      <!-- Ordinate callouts (show-all dimensions): one per unique X above the strip, its
           leader running through every instance at that X with a break at each punch -->
      @if (showDims) {
      <g class="dim">
        @for (d of dimXs; track $index) {
        <path
          class="dim__ext"
          [attr.d]="d.ext"
        />
        <text
          class="dim__label"
          [attr.x]="d.labelX"
          [attr.y]="d.labelY"
          [attr.transform]="'rotate(-60 ' + d.labelX + ' ' + d.labelY + ')'"
        >
          {{ d.label }}
        </text>
        }
        <!-- Full-width Y reference lines through each row, broken at each punch -->
        @for (d of dimYs; track $index) {
        <path
          class="dim__ext"
          [attr.d]="d.ext"
        />
        }
      </g>
      <!-- ...and one per unique Y, pinned to the visible right edge -->
      <g
        class="dim"
        [attr.transform]="'translate(' + rightShiftX + ',0)'"
      >
        @for (d of dimYs; track $index) {
        <path
          class="dim__ext"
          [attr.d]="'M -8 ' + d.y + ' H -3'"
        />
        <text
          class="dim__label dim__label--y"
          [attr.x]="-11"
          [attr.y]="d.labelY"
        >
          {{ d.label }}
        </text>
        }
      </g>
      }

      <!-- Holes, exactly as the solver receives them -->
      @for (h of holes; track $index) {
      <!-- Details go to the corner info card (not a cursor tooltip) so they never cover
           the dimension graphics -->
      <g
        class="hole"
        [class.hole--nominal]="!h.trueSize"
        (mouseenter)="onHoleEnter(h)"
        (mouseleave)="onHoleLeave()"
      >
        @if (h.path) {
        <path
          class="hole__ring"
          [attr.d]="h.path"
        />
        } @else {
        <circle
          class="hole__ring"
          [attr.cx]="h.cx"
          [attr.cy]="h.cy"
          [attr.r]="h.r"
        />
        }
        <path
          class="hole__center"
          [attr.d]="
            'M ' + (h.cx - h.arm) + ' ' + h.cy + ' H ' + (h.cx + h.arm) +
            ' M ' + h.cx + ' ' + (h.cy - h.arm) + ' V ' + (h.cy + h.arm)
          "
        />
        <!-- Invisible hit target so tiny holes are still easy to hover -->
        <circle
          class="hole__hit"
          [attr.cx]="h.cx"
          [attr.cy]="h.cy"
          [attr.r]="h.arm + 5"
        />
      </g>
      }

      <!-- Hovered-hole dimensions: leading/trailing X chain, center/nearest-edge Y chain,
           and bend-line → hole for flange-referenced punches -->
      @for (d of hoverDims; track $index) {
      <g class="dim dim--hover">
        <path
          class="dim__ext"
          [attr.d]="d.ext"
        />
        <line
          class="dim__line"
          [attr.x1]="d.x1"
          [attr.y1]="d.y1"
          [attr.x2]="d.x2"
          [attr.y2]="d.y2"
          marker-start="url(#fvvDimArrow)"
          marker-end="url(#fvvDimArrow)"
        />
        <text
          class="dim__label"
          [attr.x]="d.labelX"
          [attr.y]="d.labelY"
          [attr.text-anchor]="d.anchor"
        >
          {{ d.label }}
        </text>
      </g>
      }
    </svg>
    }
    </div>

    <!-- Hovered-hole details, pinned to the corner opposite the hole -->
    @if (hoverInfo.length) {
    <div
      class="fvv__hole-info"
      [class.fvv__hole-info--left]="hoverInfoLeft"
    >
      @for (line of hoverInfo; track $index) {
      <div [class.fvv__hole-info-head]="$index === 0">{{ line }}</div>
      }
    </div>
    }
  </div>
</div>
